import { Dialog, Button, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useState } from "react";
import { Vessel } from "../../../common/Vessel";
import { useCreateJobOrderMutation } from "../../../redux/api/jobOrder";
import formData from "./formValidation/formData";
import { Form, Formik } from "formik";
import initialValues from "./formValidation/initialValues";
import validationSchema from "./formValidation/validationSchema";
import InputField from "../../../common/FormFields/InputField";
import AutocompleteField from "../../../common/FormFields/AutocompleteField";
import {
  JobOrder,
  JobRating,
  JobRatingLabel,
  Publish,
  UnLicensedJobType,
} from "../../../common/JobOrder";
import MarinerAutocompleteField from "../../../common/FormFields/MarinerAutocompleteField";
import "./ShortTermJobOrderModal.scss";

interface ShortTermJobOrderModalProps {
  vessel: Vessel;
  children: Element | any;
}

const ShortTermJobOrderModal = (props: ShortTermJobOrderModalProps) => {
  const [open, setOpen] = useState(false);

  const permittedShortTermJobRatings = [
    JobRating._3M,
    JobRating._PM,
    JobRating._PRE,
    JobRating._AB,
    JobRating._OL,
    JobRating._OMU,
    JobRating._GUDE,
    JobRating._SA,
    JobRating._CC,
    JobRating._STB,
    JobRating._BSN,
    JobRating._QMED,
    JobRating._DECKCADET,
    JobRating._ENGINECADET,
    JobRating._TAPP
  ];

  const { formId, formField } = formData;
  const { fillByDate, onsigner, rating, port } = formField;

  const [createJobOrder] = useCreateJobOrderMutation();

  const buildUpdatedJobOrder = (values: any) => {
    const jobOrder: JobOrder = {
      createdDate: "",
      fillByDate: values.fillByDate,
      rating: values.rating,
      onsigner: values.onsigner,
      port: values.port,
      vessel: props.vessel,
      assigneeId: props.vessel.defaultAssigneeId,
      publish: Publish.CREATED,
      jobType: UnLicensedJobType.STANDBY,
      deleted: false,
      reliefTime: 0,
      smis: "",
      offsigner: undefined,
      onsignerOnboarded: undefined,
      onsignerLeavingFrom: undefined,
      onsignerDestination: undefined,
      onsignerTravelTimestamp: undefined,
      onsignerAccomodation: undefined,
      onsignerPickupDate: undefined,
      onsignerDropoffDate: undefined,
      onsignerNotes: undefined,
      onsignerUnionHall: undefined,
      offsignerDeboarded: undefined,
      offsignerLeavingFrom: undefined,
      offsignerDestination: undefined,
      offsignerTravelTimestamp: undefined,
      offsignerAccomodation: undefined,
      offsignerPickupDate: undefined,
      offsignerDropoffDate: undefined,
      offsignerNotes: undefined,
      checklistRoot: undefined,
      travelDocuments: [],
      reliefReason: undefined,
      flightNotice: undefined,
      covid19Testing: undefined,
      testingLocation: undefined,
      note: undefined,
      extra: false,
    };
    return jobOrder;
  };

  async function _submitForm(values: any, actions: any) {
    const result = await createJobOrder(buildUpdatedJobOrder(values));
    if ("error" in result) {
      actions.setSubmitting(false);
      return;
    }
    actions.setSubmitting(false);
    toast.success(<p>Short Term position created successfully</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
    handleClose();
  }

  function _handleSubmit(values: any, actions: any) {
    _submitForm(values, actions);
  }

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <span onClick={() => setOpen(true)}>{props.children}</span>
      <Dialog open={open} onClose={handleClose} className="st-modal">
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, actions) => _handleSubmit(values, actions)}
        >
          {({ isSubmitting, values, setFieldValue, errors }) => (
            <Form id={formId}>
              <div className="modal-header">
                <h2>Short-Term Fill</h2>
                <Button onClick={handleClose} className="grey-link-button">
                  <CloseIcon />
                </Button>
              </div>
              {isSubmitting && <CircularProgress size={24} />}
              <main>
                <div className="form-min">
                  <label>
                    <span className="required">{fillByDate.label}</span>
                    <InputField
                      name={fillByDate.name}
                      fullWidth
                      size="small"
                      type="date"
                    />
                  </label>
                  <label>
                    <span className="required">{rating.label}</span>
                    <AutocompleteField
                      name={rating.name}
                      data={permittedShortTermJobRatings}
                      setFieldValue={setFieldValue}
                      getOptionLabel={(option: string) =>
                        JobRatingLabel[
                          JobRating[option as keyof typeof JobRating]
                        ]
                      }
                      isOptionEqualToValue={(opt: string, val: string) =>
                        opt === val
                      }
                    />
                  </label>
                  <label>
                    <span className="required">{port.label}</span>
                    <AutocompleteField
                      name={port.name}
                      data={props.vessel.ports}
                      setFieldValue={setFieldValue}
                      getOptionLabel={(port: string) => (port ? port : "")}
                      isOptionEqualToValue={(opt: string, val: string) =>
                        opt === val
                      }
                    />
                  </label>
                  <label>
                    <span>{onsigner.label}</span>
                    <MarinerAutocompleteField
                      id="onsignerAutocomplete"
                      name={onsigner.name}
                      marinerRating={
                        values.rating ? (values.rating as any) : JobRating._SBY
                      }
                      setFieldValue={setFieldValue}
                    />
                  </label>
                </div>
              </main>
              <div className="modal-footer">
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  className="outline-button-small"
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  className="blue-button-small"
                  type="submit"
                  form="shortTermJobOrderForm"
                  disabled={isSubmitting}
                >
                  Save
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ShortTermJobOrderModal;
